import React, { useState } from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import type { CustomTemplateScope, TemplateLookup, TemplateScope } from '../../../models/TemplateLibrary'
import { TemplateScopeMapping } from './TemplateScopeMapping'
import { useReplacementOptions, type ReplacementOption } from './useReplacementOptions'

export interface TemplateChangeAccessModalProps {
  show: boolean
  loading: boolean
  templateValue: TemplateLookup
  referenceCount: number
  onConfirm: (targetScope: CustomTemplateScope, replaceWithTemplateId?: string) => void
  onCancel: () => void
}

export const TemplateChangeAccessModal: React.FC<TemplateChangeAccessModalProps> = ({ show, loading, templateValue, referenceCount, onConfirm, onCancel }) => {
  const currentScope = templateValue.scope as TemplateScope
  const targetScope: CustomTemplateScope = currentScope === 'account' ? 'user' : 'account'
  const needsReplacement = targetScope === 'user' && referenceCount > 0

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  const { replacementOptions, defaultReplacement } = useReplacementOptions({
    templateValue,
    scopes: ['account', 'system'],
    skip: !needsReplacement,
  })

  const effectiveSelectedId = selectedTemplateId ?? defaultReplacement?.id ?? null

  const handleConfirm = () => {
    if (needsReplacement) {
      if (!effectiveSelectedId) {
        return
      }
      onConfirm(targetScope, effectiveSelectedId)
    } else {
      onConfirm(targetScope)
    }
  }

  const body = (
    <Container className='d-flex flex-column gap-3'>
      <Row className='m-0'>
        <Col className='p-0 d-flex align-items-center gap-3'>
          <ScopeChip scope={currentScope} />
          <i className='bi bi-arrow-right' />
          <ScopeChip scope={targetScope} />
        </Col>
      </Row>
      {needsReplacement ? (
        <ReplacementBody
          referenceCount={referenceCount}
          replacementOptions={replacementOptions}
          defaultReplacement={defaultReplacement}
          onSelect={setSelectedTemplateId}
        />
      ) : (
        <NoReplacementMessage targetScope={targetScope} />
      )}
    </Container>
  )

  return (
    <Modal
      header={<BootstrapModal.Title>{titleFor(targetScope)}</BootstrapModal.Title>}
      body={body}
      show={show}
      onHide={onCancel}
      footerPrimaryActionTitle='Confirm'
      footerPrimaryActionLoading={loading}
      footerPrimaryActionDisabled={loading || (needsReplacement && !effectiveSelectedId)}
      footerSecondaryActionDisabled={loading}
      showFooterActions={true}
      centered
      onFooterPrimaryAction={handleConfirm}
      onFooterSecondaryAction={onCancel}
      headerBodySeparator={true}
    />
  )
}

const titleFor = (targetScope: CustomTemplateScope): string =>
  targetScope === 'user' ? 'Change this template to personal?' : 'Make this template available to your account?'

interface ScopeChipProps {
  scope: TemplateScope
}

const ScopeChip: React.FC<ScopeChipProps> = ({ scope }) => {
  const mapping = TemplateScopeMapping[scope]
  return (
    <span className={`template-badge ${mapping.color} zocks-text-gray-900`}>
      <i className={`bi ${mapping.icon} me-1`} />
      {mapping.title}
    </span>
  )
}

interface NoReplacementMessageProps {
  targetScope: CustomTemplateScope
}

const NoReplacementMessage: React.FC<NoReplacementMessageProps> = ({ targetScope }) => {
  const message = targetScope === 'user' ? 'This template will only be visible to you.' : 'Everyone in your account will be able to find and use this template.'
  return (
    <Row className='m-0'>
      <Col className='p-0 paragraph-medium zocks-text-gray-900'>{message}</Col>
    </Row>
  )
}

interface ReplacementBodyProps {
  referenceCount: number
  replacementOptions?: ReplacementOption[]
  defaultReplacement: ReplacementOption | null
  onSelect: (templateId: string | null) => void
}

const ReplacementBody: React.FC<ReplacementBodyProps> = ({ referenceCount, replacementOptions, defaultReplacement, onSelect }) => {
  return (
    <>
      <Row className='m-0'>
        <Col className='p-0 paragraph-medium zocks-text-gray-900'>
          This template is <strong>used in {referenceCount} meeting types</strong> as an account default or assigned to other users. Choose a replacement for
          all of them.
        </Col>
      </Row>
      <Row className='m-0'>
        <Col className='p-0'>
          <Form.Label className='d-block'>Replace with</Form.Label>
          {replacementOptions ? (
            <SelectDropdown
              initialSelectedOption={defaultReplacement}
              options={replacementOptions}
              onOptionSelected={(option) => onSelect(option.id)}
              placeholder='Select template'
            />
          ) : (
            <Spinner />
          )}
        </Col>
      </Row>
    </>
  )
}
